import { Select, SelectContent, SelectItem, SelectSeparator, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { SERVICE_BRANCHES, getRankGroupsForBranch } from '@/lib/military-constants';

type Props = {
  branchOfService: string;
  rank: string;
  isRetired: boolean;
  onBranchChange: (value: string) => void;
  onRankChange: (value: string) => void;
  onRetiredChange: (value: boolean) => void;
  branchRequired?: boolean;
  rankRequired?: boolean;
  branchError?: string;
  rankError?: string;
};

export default function MilitaryFields({
  branchOfService,
  rank,
  isRetired,
  onBranchChange,
  onRankChange,
  onRetiredChange,
  branchRequired = false,
  rankRequired = false,
  branchError,
  rankError,
}: Props) {
  const rankGroups = getRankGroupsForBranch(branchOfService);

  const handleBranchChange = (value: string) => {
    const newBranch = value === '__none__' ? '' : value;
    // Parent's onBranchChange is responsible for resetting rank
    onBranchChange(newBranch);
  };

  const handleRankChange = (value: string) => {
    onRankChange(value === '__none__' ? '' : value);
  };

  // Build the branch display label
  const branchLabel = branchOfService
    ? SERVICE_BRANCHES.find((b) => b.value === branchOfService)
      ? `${branchOfService}${SERVICE_BRANCHES.find((b) => b.value === branchOfService)!.label !== branchOfService ? ` — ${SERVICE_BRANCHES.find((b) => b.value === branchOfService)!.label}` : ''}`
      : branchOfService
    : '';

  return (
    <div className="space-y-3">
      {/* Service Branch */}
      <div className="space-y-1.5">
        <Label className="text-sm font-medium">
          Service Branch{branchRequired ? ' *' : ''}
        </Label>
        <Select value={branchOfService || '__none__'} onValueChange={handleBranchChange}>
          <SelectTrigger className={`h-10 ${branchError ? 'border-destructive' : ''}`}>
            <SelectValue placeholder="Select service branch">
              {branchOfService ? branchLabel : 'None'}
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__none__">None</SelectItem>
            {SERVICE_BRANCHES.map((b) => (
              <SelectItem key={b.value} value={b.value}>
                {b.value === b.label ? b.value : `${b.value} — ${b.label}`}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {branchError && <p className="text-xs text-destructive">{branchError}</p>}
      </div>

      {/* Retired Checkbox */}
      <div className="flex items-center space-x-2">
        <Checkbox
          id="isRetired"
          checked={isRetired}
          onCheckedChange={(checked) => onRetiredChange(checked === true)}
        />
        <Label htmlFor="isRetired" className="text-sm font-medium cursor-pointer">
          Check this box if you are Retired
        </Label>
      </div>

      {/* Title / Military Rank */}
      <div className="space-y-1.5">
        <Label className="text-sm font-medium">
          Title / Military Rank{rankRequired ? ' *' : ''}
        </Label>
        <Select value={rank || '__none__'} onValueChange={handleRankChange}>
          <SelectTrigger className={`h-10 ${rankError ? 'border-destructive' : ''}`}>
            <SelectValue placeholder="Select rank or title">
              {rank || 'None'}
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__none__">None</SelectItem>
            {rankGroups.map((group, i) => (
              <div key={group.category}>
                {i > 0 && <SelectSeparator />}
                <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground">
                  {group.category}
                </div>
                {group.ranks.map((r) => (
                  <SelectItem key={r} value={r}>{r}</SelectItem>
                ))}
              </div>
            ))}
          </SelectContent>
        </Select>
        {rankError && <p className="text-xs text-destructive">{rankError}</p>}
      </div>
    </div>
  );
}
